/**
 * A deliberately tiny markup subset for staff-authored copy: terms and conditions bodies and
 * rich-text blocks.
 *
 * It exists because both alternatives are worse. Accepting HTML would mean shipping a sanitiser
 * into a 150 KB public bundle and betting an applicant-facing page on its allow-list; the previous
 * renderer avoided that bet by printing the raw string, which collapsed every newline and turned a
 * fifteen-clause terms document into one unreadable paragraph.
 *
 * Parsing to an AST that a React component renders as *elements* means no HTML string exists
 * anywhere in the pipeline, so there is no injection path to get wrong — the concern the old
 * `rich_text` renderer left a comment about.
 *
 * Supported, and nothing else:
 *
 *     # ## ###          headings
 *     1.  1)            ordered list — the numbered clauses a policy document needs
 *     -  *  •           bullet list
 *     **bold**          strong
 *     *italic*  _it_    emphasis
 *     [label](https://) links, http/https/mailto only
 *     \*                a backslash makes the next character literal
 *     blank line        new paragraph
 *     single newline    line break within the paragraph
 *
 * The last rule is the one place this departs from Markdown, on purpose. The author is a programme
 * coordinator typing into a textarea, not writing Markdown; Markdown would silently reflow their
 * line breaks into one another and they would have no idea why.
 */

export type InlineNode =
  | { readonly kind: "text"; readonly text: string }
  | { readonly kind: "break" }
  | { readonly kind: "strong"; readonly children: readonly InlineNode[] }
  | { readonly kind: "em"; readonly children: readonly InlineNode[] }
  | { readonly kind: "link"; readonly href: string; readonly children: readonly InlineNode[] };

export type BlockNode =
  | { readonly kind: "paragraph"; readonly children: readonly InlineNode[] }
  | {
      readonly kind: "heading";
      readonly level: 1 | 2 | 3;
      readonly children: readonly InlineNode[];
    }
  | {
      readonly kind: "list";
      readonly ordered: boolean;
      readonly start: number;
      readonly items: readonly (readonly InlineNode[])[];
    };

const HEADING = /^(#{1,3})\s+(.+)$/;
const ORDERED = /^(\d{1,3})[.)]\s+(.+)$/;
const BULLET = /^[-*•]\s+(.+)$/;
const LINK = /^\[([^\]\n]*)\]\(([^)\s]+)\)/;

/** Anything else — `javascript:`, `data:`, a bare `//host` — is rendered as literal text instead. */
const SAFE_HREF = /^(?:https?:\/\/|mailto:)/i;

/**
 * Exported so anything that rewrites an href *after* parsing can re-apply the same rule.
 *
 * A broadcast merges per-recipient values into link targets, which means the string checked at
 * parse time is not the string that ends up in the message — and a merge field sitting at the
 * start of a URL could otherwise turn a checked link into an unchecked one.
 */
export const isSafeHref = (href: string): boolean => SAFE_HREF.test(href);

export const parseInline = (source: string): InlineNode[] => {
  const nodes: InlineNode[] = [];
  let pending = "";
  let index = 0;

  const flush = (): void => {
    if (pending !== "") {
      nodes.push({ kind: "text", text: pending });
      pending = "";
    }
  };

  while (index < source.length) {
    const character = source[index];

    if (character === "\\" && index + 1 < source.length) {
      pending += source.slice(index + 1, index + 2);
      index += 2;
      continue;
    }

    if (character === "\n") {
      flush();
      nodes.push({ kind: "break" });
      index += 1;
      continue;
    }

    if (character === "*" && source[index + 1] === "*") {
      const close = source.indexOf("**", index + 2);
      if (close > index + 2) {
        flush();
        nodes.push({ kind: "strong", children: parseInline(source.slice(index + 2, close)) });
        index = close + 2;
        continue;
      }
    }

    if (character === "*" || character === "_") {
      const close = source.indexOf(character, index + 1);
      if (close > index + 1) {
        flush();
        nodes.push({ kind: "em", children: parseInline(source.slice(index + 1, close)) });
        index = close + 1;
        continue;
      }
    }

    if (character === "[") {
      const match = LINK.exec(source.slice(index));
      const href = match?.[2] ?? "";
      if (match && SAFE_HREF.test(href)) {
        flush();
        nodes.push({ kind: "link", href, children: parseInline(match[1] ?? "") });
        index += match[0].length;
        continue;
      }
    }

    pending += character;
    index += 1;
  }

  flush();
  return nodes;
};

/** Mutable while the line loop runs; frozen into `BlockNode`s at the end. */
type RawBlock =
  | { kind: "paragraph"; lines: string[] }
  | { kind: "heading"; level: 1 | 2 | 3; text: string }
  | { kind: "list"; ordered: boolean; start: number; items: string[] };

export const parseMarkup = (source: string): BlockNode[] => {
  const raw: RawBlock[] = [];
  // Whether the previous line was blank. Only paragraphs care: a blank line between two numbered
  // clauses is how most people space a policy out, and breaking the list there would restart the
  // numbering at 1 halfway down the page.
  let afterBlank = true;

  for (const rawLine of source.split(/\r?\n/)) {
    const line = rawLine.trim();
    const tail = raw[raw.length - 1];

    if (line === "") {
      afterBlank = true;
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      raw.push({
        kind: "heading",
        level: (heading[1]?.length ?? 1) as 1 | 2 | 3,
        text: heading[2] ?? "",
      });
      afterBlank = true;
      continue;
    }

    const ordered = ORDERED.exec(line);
    if (ordered) {
      const item = ordered[2] ?? "";
      if (tail?.kind === "list" && tail.ordered) tail.items.push(item);
      else raw.push({ kind: "list", ordered: true, start: Number(ordered[1]), items: [item] });
      afterBlank = false;
      continue;
    }

    const bullet = BULLET.exec(line);
    if (bullet) {
      const item = bullet[1] ?? "";
      if (tail?.kind === "list" && !tail.ordered) tail.items.push(item);
      else raw.push({ kind: "list", ordered: false, start: 1, items: [item] });
      afterBlank = false;
      continue;
    }

    // A plain line directly under a list item is that item's wrapped continuation, not a new
    // paragraph — long clauses wrap, and the author did not mean to break out of the list.
    if (!afterBlank && tail?.kind === "list" && tail.items.length > 0) {
      tail.items[tail.items.length - 1] = `${tail.items[tail.items.length - 1] ?? ""} ${line}`;
      continue;
    }

    if (!afterBlank && tail?.kind === "paragraph") tail.lines.push(line);
    else raw.push({ kind: "paragraph", lines: [line] });
    afterBlank = false;
  }

  return raw.map((block): BlockNode => {
    if (block.kind === "heading") {
      return { kind: "heading", level: block.level, children: parseInline(block.text) };
    }
    if (block.kind === "list") {
      return {
        kind: "list",
        ordered: block.ordered,
        start: block.start,
        items: block.items.map(parseInline),
      };
    }
    return { kind: "paragraph", children: parseInline(block.lines.join("\n")) };
  });
};

/** True when the source produces no renderable content — used to skip an empty terms box. */
export const isMarkupEmpty = (source: string): boolean => source.trim() === "";
